import React, { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from 'recharts'
import { cn } from '@/lib/utils'
import { currencySymbol, formatRateSym } from '@/lib/calendar-utils'
import { stageLabel, STAGE_LABELS as STEP_NAMES } from '@/lib/pipeline-utils'
import type { Adjustment } from '@/types/calendar'

interface RateWaterfallChartProps {
  adjustments: Adjustment[]
  currency: string
}

// The short name under each bar. Every stage the engine stamps has one
// (test/lib/frontend_waterfall_stage_labels_convention_test.rb reads the engine's stage keys against
// this map), so an internal key like `same_room` never reaches an operator.
const STAGE_LABELS: Record<string, string> = {
  base_rate: 'Base',
  linked_parent: 'Parent',
  // Combined inventory: a Same room's price is its twin's, copied, plus any offset from it.
  same_room: 'Same room',
  same_room_offset: 'Offset',
  seasonal: 'Season',
  day_of_week: 'Day',
  property_demand: 'Demand',
  room_demand: 'Room',
  target_pressure: 'Target',
  clearance: 'Clearance',
  event: 'Event',
  market_position: 'Market',
  orphan_day: 'Gap',
  derived_offset: 'Derived',
  derived_rate_plan: 'Plan',
  clamp: 'Limits',
  rounding: 'Round',
  override: 'Override',
  rate_stability: 'Steady',
  restrictions: 'Restrictions',
}

// Stages whose full name is the step's own server sentence, as the day breakdown words it:
// "Same room as Double", "Offset from Double".
const NAMED_BY_STEP = ['same_room', 'same_room_offset']

/** A Same room's percentage offset in the server's own spelling ("+10%"), where the step stored one.
 *  An amount offset is already the bar's money, so it adds nothing beside it. */
function offsetPct(adj: Adjustment): string | null {
  if (adj.stage !== 'same_room_offset' || adj.offset_type === 'absolute') return null
  return adj.offset_label ?? null
}

/** The bar's short name: never the raw stage key, even for a stage this chart has not met. */
function shortLabel(adj: Adjustment): string {
  return STAGE_LABELS[adj.stage] ?? STEP_NAMES[adj.stage] ?? (adj.description || 'Other step')
}

function stageColor(delta: number, stage: string): string {
  // A Same room's copied price is its base, as in the day breakdown's Base block.
  if (stage === 'base_rate' || stage === 'linked_parent' || stage === 'same_room') return '#0d9488'
  if (stage === 'clamp' || stage === 'rounding') return '#94a3b8'
  if (stage === 'override') return '#f59e0b'
  if (delta > 0) return '#10b981'
  if (delta < 0) return '#f43f5e'
  return '#cbd5e1'
}

const WaterfallTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">{d.title}</p>
      {d.description && d.description !== d.title && <p className="text-[11px] text-muted-foreground">{d.description}</p>}
      <p className="text-sm font-semibold tabular-nums text-foreground">{formatRateSym(d.runningTotal, d.sym)}</p>
      {d.signedDelta !== 0 && (
        <p className={cn('text-xs font-semibold tabular-nums', d.signedDelta > 0 ? 'text-emerald-600' : 'text-rose-600')}>
          {d.signedDelta > 0 ? '+' : '-'}{formatRateSym(Math.abs(d.signedDelta), d.sym)}{d.pct ? ` (${d.pct})` : ''}
        </p>
      )}
    </div>
  )
}

export interface WaterfallDatum {
  /** The short name under the bar. */
  label: string
  /** The tooltip's name for the step: the short name, or the step's own sentence where the day
   *  breakdown uses one ("Same room as Double", "Offset from Double"). */
  title: string
  stage: string
  base: number
  /** Absolute magnitude of the stage delta — bar GEOMETRY only (height). */
  delta: number
  /** Signed stage delta (may be negative). Drives the tooltip sign char + color
   *  and the bar Cell fill — a discount reads as a rose bar AND a rose "-£6". */
  signedDelta: number
  runningTotal: number
  description: string
  pct: string
  sym: string
}

/**
 * Build the per-stage waterfall rows from backend adjustments. Pure — no rate
 * math beyond deltas used for bar GEOMETRY. `runningTotal` is the canonical
 * backend money; `pct` is the backend-stamped modifier_pct for the stage (blank
 * when the stage carries none — base, clamp, rounding, override, …) so the label
 * never shows a percentage derived from currency deltas.
 */
export function buildWaterfallData(adjustments: Adjustment[], sym: string): WaterfallDatum[] {
  if (!adjustments?.length) return []

  // Filter to stages that have running_total
  const stages = adjustments.filter((a) => a.running_total != null)
  if (stages.length === 0) return []

  const result: WaterfallDatum[] = []

  for (let i = 0; i < stages.length; i++) {
    const adj = stages[i]
    const prev = i > 0 ? stages[i - 1].running_total! : 0
    const current = adj.running_total!
    const delta = i === 0 ? current : current - prev

    const label = shortLabel(adj)
    const named = NAMED_BY_STEP.includes(adj.stage)

    result.push({
      label,
      title: named ? stageLabel(adj.stage, adj.description || '') : label,
      stage: adj.stage,
      base: i === 0 ? 0 : Math.min(prev, current),
      delta: Math.abs(delta),
      signedDelta: delta,
      runningTotal: current,
      description: adj.description || '',
      pct: offsetPct(adj) ?? (adj.modifier_pct != null ? `${adj.modifier_pct}%` : ''),
      sym,
    })
  }

  return result
}

export default function RateWaterfallChart({ adjustments, currency }: RateWaterfallChartProps) {
  const sym = currencySymbol(currency)

  const data = useMemo(() => buildWaterfallData(adjustments, sym), [adjustments, sym])

  if (data.length === 0) return null

  const finalRate = data[data.length - 1]?.runningTotal ?? 0

  return (
    <div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} barSize={24}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `${sym}${v}`}
            width={50}
          />
          <Tooltip content={<WaterfallTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
          <Bar dataKey="base" stackId="a" fill="transparent" />
          <Bar dataKey="delta" stackId="a">
            {data.map((entry, i) => (
              <Cell key={i} fill={stageColor(entry.signedDelta, entry.stage)} />
            ))}
          </Bar>
          <ReferenceLine y={finalRate} stroke="#0d9488" strokeDasharray="3 3" strokeWidth={1} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
